import { Box, Dialog, DialogTitle, Button, DialogActions } from '@mui/material';
import { useFieldArray, useForm } from 'react-hook-form';
import { ScrollbarWrapper } from 'src/components';
import { DialogContentElement } from './DialogContentElement';
import { FormProvider } from 'src/components/minimals/form';
import { useEffect, useMemo } from 'react';
import { FormValuesTypes } from './types';
import { PlusFillIcon } from 'src/assets/icons/PlusFillIcon';
import { useResponsive } from 'src/hooks/useResponsive';
import { useAuthentication } from 'src/features/authentication/context';
import { WidgetService } from 'src/services/widgets/widgets.service';
import { WidgetType, WidgetTypeEnum } from 'src/features/sites/types/types';
import { useGetAssetByIdQuery } from 'src/hooks/reactQueryApiHooks';

type Props = {
  isOpen: boolean;
  onToggle: () => void;
  toggleAddOrRemoveHandler: () => void;
  selectedAssetForAssetController: string;
  isEdit: boolean;
  existingWidget?: WidgetType;
  position?: {
    x: number;
    y: number;
  };
};

export const AssetControllerWidgetPopup = ({
  isOpen,
  onToggle,
  selectedAssetForAssetController,
  isEdit,
  existingWidget,
  position,
  toggleAddOrRemoveHandler,
}: Props) => {
  const isMobile = useResponsive('down', 'sm');
  const {
    customerId: { value: customerId },
    siteId: { value: siteId },
  } = useAuthentication();

  const defaultValues: FormValuesTypes = useMemo(
    () => ({
      assetId: '',
      controllerId: '',
      showAlarmStatus: true,
      widgetSubType: '',
      tags: [{ label: '', id: '', position: 0 }],
    }),
    []
  );

  const methods = useForm<any>({
    defaultValues: defaultValues,
  });

  const { handleSubmit, reset, watch } = methods;
  const watchedAssetId = watch('assetId');

  const { asset } = useGetAssetByIdQuery({
    customerId: customerId!,
    siteId: siteId!,
    assetId: watchedAssetId,
  });

  const assetType = asset?.assetType;

  const { fields, append, remove } = useFieldArray({ control: methods.control, name: 'tags' });

  const onSubmitHandler = async (values: FormValuesTypes) => {
    if (!customerId || !siteId) return;

    const tagsWithPosition = values.tags?.map((tag) => ({
      ...tag,
      position: tag.position ?? 0,
    }));

    const connectedTo = {
      assetId: values.assetId,
      controllerId: values.controllerId,
      showAlarmStatus: values.showAlarmStatus,
      liveDataTags: tagsWithPosition?.filter((tag) => tag.id).map(({ tagId, ...rest }) => rest),
    };

    const payloadData = {
      widgetType: WidgetTypeEnum.ASSET_CONTROLLER,
      widgetSubType: values.widgetSubType,
      connectedTo,
      positioning:
        isEdit && existingWidget?.positioning
          ? existingWidget.positioning
          : {
              x: position?.x ?? 0,
              y: position?.y ?? 0,
              width: 4,
              height: 4.4,
            },
    };

    try {
      if (isEdit && existingWidget?.id) {
        await WidgetService.edit({
          widgetId: existingWidget.id,
          customerId,
          siteId,
          data: payloadData,
        });
      } else {
        await WidgetService.add({
          customerId,
          siteId,
          data: payloadData,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      onToggle();
      toggleAddOrRemoveHandler();
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    if (isEdit && existingWidget) {
      const existingTags = existingWidget.connectedTo.liveDataTags ?? [];

      reset({
        assetId: existingWidget.connectedTo.assetId,
        controllerId: existingWidget.connectedTo.controllerId,
        showAlarmStatus: existingWidget.connectedTo.showAlarmStatus,
        widgetSubType: existingWidget.widgetSubType,
        tags: [
          ...existingTags,
          ...Array.from({ length: Math.max(0, 4 - existingTags.length) }, () => ({
            label: '',
            id: '',
            position: 0,
          })),
        ],
      });
    } else {
      reset({
        assetId: selectedAssetForAssetController,
        controllerId: '',
        showAlarmStatus: true,
        widgetSubType: '',
        tags: Array.from({ length: 4 }, () => ({
          label: '',
          id: '',
          position: 0,
        })),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Dialog
      fullWidth
      onClose={onToggle}
      open={isOpen}
      sx={{ display: 'flex', justifyContent: 'center' }}
      PaperProps={{
        sx: {
          maxWidth: isMobile ? '350px !important' : '100%',
          width: '100%',
          margin: '0 auto',
        },
      }}
    >
      <ScrollbarWrapper>
        <Box>
          <DialogTitle sx={{ fontSize: isMobile ? '16px' : '18px' }}>Edit Widget</DialogTitle>
        </Box>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmitHandler)}>
          <DialogContentElement
            fields={fields as { id: string; label: string; tagId: string }[]}
            append={append}
            remove={remove}
            assetType={assetType!}
          />

          <DialogActions>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <Button
                size={'medium'}
                variant="text"
                startIcon={<PlusFillIcon />}
                data-sm="add-new-tag"
                data-testid="add-new-tag"
                onClick={() => append({ label: '', id: '', position: 0 })}
                disabled={fields.length >= 4}
              >
                Add New Tag
              </Button>
              <Button variant="outlined" data-sm="cancel-btn" onClick={onToggle}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" data-sm="save-btn">
                Save
              </Button>
            </Box>
          </DialogActions>
        </FormProvider>
      </ScrollbarWrapper>
    </Dialog>
  );
};
